import {
  callToolCallback,
  type OnBeforeRunResult,
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import type { Tool } from '@trimble-agentic-internal-npm-local/agentic-chat-ui';

// Define the return type based on the RunContextCallback return type
type DynamicRunContext = {
  tools: {
    runTime: {
      [toolCallId: string]: {
        definition: Tool;
        callback: (args: Record<string, unknown>) => Promise<string>;
        timeOutInMs?: number;
      };
    };
    global: {
      [toolCallId: string]: {
        callback: (args: Record<string, unknown>) => Promise<string>;
        timeOutInMs?: number;
      };
    };
  };
  runContext?: {
    context: Array<{
      description: string;
      value: string;
    }>;
  };
};

/**
 * Maps dynamic tools from the OnBeforeRun result to the run context format
 * expected by the chat UI components.
 *
 * This function transforms serializable tool configurations (without callbacks)
 * into runtime tool configurations with proper callback handlers.
 *
 * @param dynamicRunContext - The result from callOnBeforeRun containing serializable tools
 * @returns A DynamicRunContext with tools that include callback handlers
 */
export function mapDynamicToolsToRunContext(
  dynamicRunContext: OnBeforeRunResult
): DynamicRunContext {
  const result: DynamicRunContext = {
    tools: {
      runTime: {},
      global: {},
    },
    runContext: dynamicRunContext.runContext,
  };

  const runTimeTools = dynamicRunContext.tools?.runTime || {};

  for (const runTimeTool in runTimeTools) {
    result.tools.runTime[runTimeTool] = {
      definition: runTimeTools[runTimeTool].definition as Tool,
      timeOutInMs: runTimeTools[runTimeTool].timeOutInMs,
      callback: async (args: Record<string, unknown>) => {
        return await callToolCallback(
          runTimeTool,
          args,
          runTimeTools[runTimeTool].timeOutInMs
        );
      },
    };
  }

  const globalTools = dynamicRunContext.tools?.global || {};

  for (const globalTool in globalTools) {
    result.tools.global[globalTool] = {
      timeOutInMs: globalTools[globalTool].timeOutInMs,
      callback: async (args: Record<string, unknown>) => {
        return await callToolCallback(
          globalTool,
          args,
          globalTools[globalTool].timeOutInMs
        );
      },
    };
  }

  return result;
}
